/**
 * Quick View Drawer Component
 * Opens a slide-in drawer with product details fetched via Section Rendering API
 */

// Global cache for quick view HTML
window.quickViewCache = window.quickViewCache || {};

class QuickViewDrawer extends HTMLElement {
  constructor() {
    super();
    this.drawer = this;
    this.content = this.querySelector('[data-quick-view-content]');
    this.closeBtn = this.querySelector('[data-close-quick-view]');
    this.titleElement = this.querySelector('.quick-view-title');
    this.overlay = document.querySelector('#site-overlay');
    this.currentProductHandle = null;
    this.cache = window.quickViewCache;
    
    this.bindEvents();
  }

  bindEvents() {
    // Close button
    this.closeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });
    
    // Close on overlay click
    this.overlay?.addEventListener('click', () => {
      if (this.classList.contains('opened-drawer')) {
        this.close();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('opened-drawer')) {
        this.close();
      }
    });

    // Delegate click events for quick view triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-quick-view-trigger]');
      if (trigger) {
        e.preventDefault();
        const productHandle = trigger.dataset.productHandle;
        if (productHandle) {
          this.open(productHandle);
        }
      }
    });

    // Close quick view when cart drawer opens (after add to cart)
    this.observeCartDrawer();
  }

  observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-form.cart-drawer');
    if (!cartDrawer) return;

    // Use MutationObserver to detect when cart drawer opens
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          if (cartDrawer.classList.contains('opened-drawer') && this.classList.contains('opened-drawer')) {
            // Keep overlay visible for cart drawer
            this.close(true);
          }
        }
      });
    });

    observer.observe(cartDrawer, { attributes: true });
  }

  async open(productHandle) {
    if (!productHandle) return;

    this.currentProductHandle = productHandle;
    
    // Show drawer with loading state
    this.classList.add('loading');
    this.classList.add('opened-drawer');
    
    // Show overlay using Utility (same as cart drawer)
    if (typeof Utility !== 'undefined' && Utility.showOverlay) {
      Utility.showOverlay();
    } else {
      this.overlay?.classList.add('overlay--body');
      document.body.classList.add('scroll-fixed');
    }

    // Trap focus
    this.setAttribute('aria-hidden', 'false');
    if (typeof Utility !== 'undefined' && Utility.forceFocus) {
      Utility.forceFocus(this.titleElement);
      Utility.trapFocus(this, this.closeBtn);
    }
    
    try {
      const html = await this.fetchProductSection(productHandle);
      if(this.content) this.content.innerHTML = html;
      
      // Re-initialize any custom elements or scripts within the content
      this.initializeContent();
      
    } catch (error) {
      console.error('Quick View: Failed to load product', error);
      if(this.content) {this.content.innerHTML = `
        <div class="text-center p-4">
          <p class="text-danger">Failed to load product. Please try again.</p>
          <a href="/products/${productHandle}" class="btn btn-primary">View Product Page</a>
        </div>
      `;}
    } finally {
      this.classList.remove('loading');
    }
  }

  async fetchProductSection(productHandle) {
    // Check cache first
    if (this.cache[productHandle]) {
      return this.cache[productHandle];
    }

    const url = `/products/${productHandle}?section_id=template-product-quick-view`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const html = await response.text();
    
    // Store in cache
    this.cache[productHandle] = html;
    
    return html;
  }

  initializeContent() {
    // Reinitialize lazy loading for images
    if (typeof lozad !== 'undefined') {
      const observer = lozad('.quick-view-content .lozad', {
        loaded: function(el) {
          el.classList.add('loaded');
        }
      });
      observer.observe();
    }

    // Reinitialize Yotpo review widgets
    if (typeof yotpoWidgetsContainer !== 'undefined' && yotpoWidgetsContainer.initWidgets) {
      yotpoWidgetsContainer.initWidgets();
    }

    // Initialize data-item-handle click handlers for product switching
    this.initProductSwitching();

    // Dispatch event for other scripts to hook into
    document.dispatchEvent(new CustomEvent('quickview:loaded', {
      detail: { handle: this.currentProductHandle, container: this.content }
    }));
  }

  // Handle product switching within quick view (for effect/potency options)
  initProductSwitching() {
    const itemHandleElements = this.content?.querySelectorAll('[data-item-handle]') || [];
    
    itemHandleElements.forEach(element => {
      element.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        
        const newHandle = element.getAttribute('data-item-handle');
        if (newHandle && newHandle !== this.currentProductHandle) {
          // Show loading state
          this.classList.add('loading');
          
          try {
            const html = await this.fetchProductSection(newHandle);
            this.currentProductHandle = newHandle;
            if(this.content) this.content.innerHTML = html;
            this.initializeContent();
          } catch (error) {
            console.error('Quick View: Failed to switch product', error);
          } finally {
            this.classList.remove('loading');
          }
        }
      });
    });
  }

  close(keepOverlay = false) {
    // Store handle before clearing
    const handleToFocus = this.currentProductHandle;
    
    this.classList.remove('opened-drawer');
    this.classList.remove('loading');
    
    // Hide overlay only if not keeping it for cart drawer
    if (!keepOverlay) {
      if (typeof Utility !== 'undefined' && Utility.hideOverlay) {
        Utility.hideOverlay();
      } else {
        this.overlay?.classList.remove('overlay--body');
        document.body.classList.remove('scroll-fixed');
      }
    }
    
    this.setAttribute('aria-hidden', 'true');
    
    // Remove focus trap (don't restore focus if cart drawer is opening)
    if (typeof Utility !== 'undefined' && Utility.removeTrapFocus && !keepOverlay) {
      const lastTrigger = document.querySelector(`[data-quick-view-trigger][data-product-handle="${handleToFocus}"]`);
      Utility.removeTrapFocus(lastTrigger);
    }
    
    // Clear content after animation
    setTimeout(() => {
      if(this.content) this.content.innerHTML = '';
      this.currentProductHandle = null;
    }, 500);
  }
}

// Register custom element
if (!customElements.get('quick-view-drawer')) {
  customElements.define('quick-view-drawer', QuickViewDrawer);
}
